import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import find from 'lodash/find';
import includes from 'lodash/includes';
import some from 'lodash/some';
import { eachHierarchy, everyHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { BLACKLISTED_UNICODE_RANGES, CLOSE_OPEN_BRACKETS_MAP, QUOTES_LIST, VALIDATE_DATA_TYPE, VALIDATION_ID_WHITELIST } from './constants';
/* eslint-disable consistent-return */

/* Pure validators functions */
const quotesValidator = (string) => {
  const unpairedQuotes = [];

  forEach(QUOTES_LIST, (c) => {
    const matched = string.match(new RegExp(c, 'g')) || [];

    if (matched.length % 2 !== 0) {
      unpairedQuotes.push(c);
    }
  });

  return unpairedQuotes.length === 0;
};

const bracketsValidator = (string) => {
  const open = [];

  const mispositioned = find([...string], (c) => {
    if (includes(CLOSE_OPEN_BRACKETS_MAP, c)) {
      open.push(c);
    } else if (CLOSE_OPEN_BRACKETS_MAP[c] && open.pop() !== CLOSE_OPEN_BRACKETS_MAP[c]) {
      return true;
    }
  });

  return !mispositioned && open.length === 0;
};

const phraseSearchValidator = (string) => {
  return /".*"/g.test(string);
};

const languageValidator = (string) => {
  const blacklistedRangesRegex = new RegExp(`[${BLACKLISTED_UNICODE_RANGES.join('')}]`, 'g');
  return !string.match(blacklistedRangesRegex);
};

/* Validator callers */
const _validateStringWithValidator = (string, validator) => {
  return validator(string);
};

const _validateAllQueryFactItemWithValidator = (queryFactItem, validator) => {
  const invalidFields = [];
  eachHierarchy(
    queryFactItem,
    (fact) => {
      forEach(fact.fields, (field) => {
        if (!VALIDATION_ID_WHITELIST[field.id] && field.dataType === VALIDATE_DATA_TYPE && !validator(get(field, 'value', ''))) {
          invalidFields.push(field);
        }
      });
    },
    null,
    { children: 'facts' }
  );

  return invalidFields.length === 0;
};

const _validateAnyQueryFactItemWithValidator = (queryFactItem, validator) => {
  let hasPhraseItem;

  everyHierarchy(
    queryFactItem,
    (fact) => {
      const hasPhraseField = some(fact.fields, (field) => field.dataType === VALIDATE_DATA_TYPE && validator(get(field, 'value', '')));

      // if field with phrase search was found - return false to break the loop
      if (hasPhraseField) {
        hasPhraseItem = true;
        return false;
      }
    },
    null,
    { children: 'facts' }
  );

  return hasPhraseItem;
};

export const validateBrackets = (query) => {
  if (isString(query)) {
    return _validateStringWithValidator(query, bracketsValidator);
  }
  if (isObject(query)) {
    return _validateAllQueryFactItemWithValidator(query, bracketsValidator);
  }
};

export const validateQuotes = (query) => {
  if (isString(query)) {
    return _validateStringWithValidator(query, quotesValidator);
  }
  if (isObject(query)) {
    return _validateAllQueryFactItemWithValidator(query, quotesValidator);
  }
};

export const validateLanguage = (query) => {
  if (isString(query)) {
    return _validateStringWithValidator(query, languageValidator);
  }
  if (isObject(query)) {
    return _validateAllQueryFactItemWithValidator(query, languageValidator);
  }
};

export const validateIsPhraseSearch = (query) => {
  if (isString(query)) {
    return _validateStringWithValidator(query, phraseSearchValidator);
  }
  if (isObject(query)) {
    return _validateAnyQueryFactItemWithValidator(query, phraseSearchValidator);
  }
};

export const insertSpaceBetweenAdjacentQuotes = (query) => {
  if (typeof query === 'string') {
    // eslint-disable-next-line array-callback-return
    return QUOTES_LIST.reduce((acc, c) => {
      if (acc.indexOf(`${c}${c}`) !== -1) {
        return acc.replace(new RegExp(`${c}${c}`, 'g'), `${c} ${c}`);
      }
    }, query);
  }
  if (typeof query === 'object') {
    return eachHierarchy(
      query,
      (fact) => {
        forEach(fact.fields, (field) => {
          if (field.dataType === VALIDATE_DATA_TYPE) {
            forEach(QUOTES_LIST, (c) => {
              if (get(field, 'value', '').indexOf(`${c}${c}`) !== -1) {
                // eslint-disable-next-line no-param-reassign
                field.value = field.value.replace(new RegExp(`${c}${c}`, 'g'), `${c} ${c}`);
              }
            });
          }
        });
      },
      null,
      { children: 'facts' }
    );
  }
};

export const showIncorrectBracketsOrQuotesMessage = (opts) => {
  const errorMessage = 'An unexpected character was found in the query.';
  return NotifyDialogsService.openModalWindow({
    title: 'Warning',
    text: `${errorMessage} Would you like to correct the query or perform the search?`,
    type: 'warning',
    cancelText: 'Correct',
    confirmText: 'Search',
    backdrop: 'static',
    ...opts,
    trackingOptions: {
      message: errorMessage,
      component: 'Query validation',
    },
  });
};

export const showIncorrectLanguageMessage = (opts = {}) => {
  return NotifyDialogsService.openModalWindow({
    type: 'warning',
    backdrop: 'static',
    title: 'Warning',
    text: 'Note: Reaxys can only accept English language for query input. Please change the query before searching.',
    confirmText: 'Ok',
    ...opts,
    trackingOptions: {
      component: 'Query validation',
    },
  });
};
